import { createDefaultApplicationPeriodTerms, getDefaultApplicationPeriod } from './applicationPeriod';
import { buildChallengeCardSubtitle, buildChallengeCardTitle } from './challengeCard';
import {
  DEFAULT_CHALLENGE_CARD_SUBTITLE_AFTER,
  DEFAULT_CHALLENGE_CARD_SUBTITLE_BEFORE,
  DEFAULT_CHALLENGE_CARD_TITLE_SUFFIX,
  DEFAULT_SECONDARY_SECTION_TITLE,
  DEFAULT_STATUS_CLAIM_HERO_IMAGE_ID,
  DEFAULT_STATUS_CLAIM_HERO_TITLE,
  DEFAULT_STATUS_CLAIM_SECONDARY_SUBTITLE,
  DEFAULT_STATUS_CLAIM_TERMS_SLUG,
  DEFAULT_STATUS_MATCH_HERO_CTA_TEXT,
  DEFAULT_STATUS_MATCH_HERO_DESCRIPTION,
  DEFAULT_STATUS_MATCH_HERO_IMAGE_ID,
  DEFAULT_STATUS_MATCH_HERO_SUBTITLE,
  DEFAULT_STATUS_MATCH_HERO_TITLE,
  DEFAULT_STATUS_MATCH_VERIFICATION_IMAGE_ID,
  SUCCESS_STEP_DEFINITIONS,
} from './constants';
import { DEFAULT_STATUS_MATCH_CONDITION_DEFINITIONS } from './defaultMatchConditions';
import { DEFAULT_STATUS_MATCH_FAQ_DEFINITIONS } from './defaultMatchFaqs';
import { DEFAULT_STATUS_MATCH_TERMS } from './defaultMatchTerms';
import { createRowId } from '../ids';
import type { ConditionRow, FaqRow, StatusCampaignPayloadBuilderState, ProgramConfig, SuccessStepRow, TextStepRow } from './types';

export function createDefaultSuccessSteps(): Array<SuccessStepRow> {
  return SUCCESS_STEP_DEFINITIONS.map((step) => ({
    id: createRowId(),
    key: step.key,
    title: step.title,
    description: step.description,
  }));
}

export function createDefaultStatusMatchFaqs(): Array<FaqRow> {
  return DEFAULT_STATUS_MATCH_FAQ_DEFINITIONS.map((faq) => ({
    id: createRowId(),
    question: faq.question,
    answer: faq.answer,
  }));
}

export function createDefaultStatusMatchConditions(): Array<ConditionRow> {
  return DEFAULT_STATUS_MATCH_CONDITION_DEFINITIONS.map((condition) => ({
    id: createRowId(),
    icon: condition.icon,
    title: condition.title,
    description: condition.description,
  }));
}

const DEFAULT_HOW_IT_WORKS: Array<Omit<TextStepRow, 'id'>> = [
  {
    title: 'Select program',
    description: 'Choose the loyalty program where you hold elite status.',
  },
  {
    title: 'Upload proof',
    description: 'Submit a screenshot showing your name, tier, and program.',
  },
  {
    title: 'Book & travel',
    description: 'Book for a minimum of 3 nights and travel within 3 months.',
  },
  {
    title: 'Status activates',
    description: "After your trip, we'll review and upgrade your tier within 4 weeks.",
  },
];

export function createDefaultStatusMatchState(): StatusCampaignPayloadBuilderState {
  const applicationPeriod = getDefaultApplicationPeriod();

  return {
    mode: 'statusMatch',
    campaignId: '',
    slug: '',
    termsSlug: 'societe-status-match-terms-and-conditions',
    heroTitle: DEFAULT_STATUS_MATCH_HERO_TITLE,
    heroSubtitle: DEFAULT_STATUS_MATCH_HERO_SUBTITLE,
    heroDescription: DEFAULT_STATUS_MATCH_HERO_DESCRIPTION,
    heroCtaText: DEFAULT_STATUS_MATCH_HERO_CTA_TEXT,
    heroImageId: DEFAULT_STATUS_MATCH_HERO_IMAGE_ID,
    allowedRegions: [],
    programs: [],
    conditions: createDefaultStatusMatchConditions(),
    howItWorks: DEFAULT_HOW_IT_WORKS.map((step) => ({ ...step, id: createRowId() })),
    terms: createDefaultApplicationPeriodTerms([...DEFAULT_STATUS_MATCH_TERMS]),
    applicationPeriodFrom: applicationPeriod.from,
    applicationPeriodTo: applicationPeriod.to,
    faqs: createDefaultStatusMatchFaqs(),
    challengeCardTitleSuffix: DEFAULT_CHALLENGE_CARD_TITLE_SUFFIX,
    challengeCardSubtitleBefore: DEFAULT_CHALLENGE_CARD_SUBTITLE_BEFORE,
    challengeCardSubtitleAfter: DEFAULT_CHALLENGE_CARD_SUBTITLE_AFTER,
    challengeCardBackground: { type: 'color', value: '#ffffff' },
    evidenceMinPhotos: 3,
    evidenceVerificationImageId: DEFAULT_STATUS_MATCH_VERIFICATION_IMAGE_ID,
    evidencePhotoHints: ['App screenshot', 'Digital card', 'Account dashboard'],
    evidenceAcceptedFormats: ['JPG', 'PNG', 'PDF'],
    evidenceProofRequirements: [
      'Your full name',
      'The loyalty program name',
      'Your eligible status tier',
      'Valid expiration date',
    ],
    successSteps: createDefaultSuccessSteps(),
    partnerName: '',
    partnerLogoImageId: '',
    tiersGranted: [],
    secondaryTitle: DEFAULT_SECONDARY_SECTION_TITLE,
    secondarySubtitle: '',
  };
}

export function createDefaultStatusClaimState(): StatusCampaignPayloadBuilderState {
  return {
    ...createDefaultStatusMatchState(),
    mode: 'statusClaim',
    termsSlug: DEFAULT_STATUS_CLAIM_TERMS_SLUG,
    heroCtaText: '',
    heroSubtitle: '',
    heroDescription: '',
    evidenceVerificationImageId: '',
    allowedRegions: [],
    programs: [],
    conditions: [],
    howItWorks: [],
    terms: [],
    applicationPeriodFrom: '',
    applicationPeriodTo: '',
    faqs: [],
    successSteps: [],
    heroTitle: DEFAULT_STATUS_CLAIM_HERO_TITLE,
    heroImageId: DEFAULT_STATUS_CLAIM_HERO_IMAGE_ID,
    secondaryTitle: DEFAULT_SECONDARY_SECTION_TITLE,
    secondarySubtitle: DEFAULT_STATUS_CLAIM_SECONDARY_SUBTITLE,
  };
}

export function createEmptyProgram(): ProgramConfig {
  return {
    id: createRowId(),
    programName: '',
    logoImageId: '',
    tierMappings: [],
  };
}

export function createDefaultState(mode: StatusCampaignPayloadBuilderState['mode'] = 'statusMatch'): StatusCampaignPayloadBuilderState {
  return mode === 'statusClaim' ? createDefaultStatusClaimState() : createDefaultStatusMatchState();
}

/** Ensures success steps exist with fixed keys for status match mode. */
export function withDefaultSuccessSteps(state: StatusCampaignPayloadBuilderState): StatusCampaignPayloadBuilderState {
  if (state.mode !== 'statusMatch') return state;
  const defaults = createDefaultSuccessSteps();
  const merged = defaults.map((defaultStep) => {
    const existing = state.successSteps.find((step) => step.key === defaultStep.key);
    return existing ?? defaultStep;
  });
  return { ...state, successSteps: merged };
}

export { buildChallengeCardTitle, buildChallengeCardSubtitle };
